import {
  MessageAttachedFile,
  MessageInlineImage,
} from 'lib-shared-legacy-types';
import flatten from 'lodash/flatten';
import uniqWith from 'lodash/uniqWith';
import { ReactNode, useEffect, useMemo, useState } from 'react';

import { prepareInlineImages } from 'app/utils/attachments';

import { createAttachedFiles } from './AttachedFile';
import { InlinedImages } from './inlinedImages/InlinedImages';

/**
 * Represents the image size, either width or height, that determines the
 * threshold of whether the image is considered tiny.
 */
const tinyImageSizeThreshold = 24;

type UseMessageFilesResult = {
  images: ReactNode;
  attachments: ReactNode[];
};

/** Creates image and attachments thumbnail as react nodes */
export function useMessageFiles(
  imagesInput?: MessageInlineImage[] | MessageInlineImage | null,
  attachmentsInput?: MessageAttachedFile[] | MessageAttachedFile | null,
): UseMessageFilesResult {
  const [renderedImageNodes, setRenderedImageNodes] = useState<
    MessageInlineImage[]
  >([]);
  const [unrenderedImageFiles, setUnrenderedImageFiles] = useState<
    MessageAttachedFile[]
  >([]);

  const images: MessageInlineImage[] = useMemo(
    () => flatten([imagesInput ?? []]),
    [imagesInput],
  );
  const attachments: MessageAttachedFile[] = useMemo(
    () => flatten([attachmentsInput ?? []]),
    [attachmentsInput],
  );

  // Handles fetching the sizes of the images and creating their compositions
  useEffect(() => {
    const prepareImages = async () => {
      const renderedImages: MessageInlineImage[] = [];
      const unrenderedImages: MessageAttachedFile[] = [];
      const uniqueFiles = uniqWith(
        prepareInlineImages(images),
        (a, b) => a.absolute_uri === b.absolute_uri,
      );

      const results = await Promise.allSettled(
        uniqueFiles.map(async (file) => {
          try {
            return {
              file,
              image: await getImage(file.absolute_uri),
            };
          } catch {
            return {
              file,
              image: null,
            };
          }
        }),
      );

      uniqWith(
        results,
        (a, b) =>
          a.status === 'fulfilled' &&
          b.status === 'fulfilled' &&
          !!a.value.image &&
          !!b.value.image &&
          a.value.image.readerResult === b.value.image.readerResult,
      ).forEach((result) => {
        if (result.status !== 'fulfilled') {
          return;
        }

        const { file, image } = result.value;

        if (!image) {
          unrenderedImages.push({
            url: file.absolute_uri,
            pretty_name: file.name,
          });

          return;
        }

        // Filter out repetitive or tiny images
        if (
          image.imageElement.width <= tinyImageSizeThreshold ||
          image.imageElement.height <= tinyImageSizeThreshold
        ) {
          return;
        }

        renderedImages.push({ url: file.absolute_uri, pretty_name: file.name });
      });

      setRenderedImageNodes(renderedImages);
      setUnrenderedImageFiles(unrenderedImages);
    };

    prepareImages();
  }, [images]);

  return useMemo(
    (): UseMessageFilesResult => ({
      images: <InlinedImages files={renderedImageNodes} />,
      attachments: createAttachedFiles([
        ...unrenderedImageFiles,
        ...attachments,
      ]),
    }),
    [attachments, renderedImageNodes, unrenderedImageFiles],
  );
}

function getImage(url: string): Promise<{
  imageElement: HTMLImageElement;
  blob: Blob;
  readerResult: FileReader['result'];
}> {
  const image = new Image();

  return new Promise((resolve, reject) => {
    image.src = url;

    image.onload = () => {
      fetch(url)
        .then(async (response) => {
          const blob = await response.blob();
          const reader = new FileReader();

          reader.onloadend = () => {
            resolve({
              imageElement: image,
              blob,
              readerResult: reader.result,
            });
          };
          reader.readAsDataURL(blob);
        })
        .catch(reject);

      image.remove();
    };

    image.onerror = () => {
      reject();
      image.remove();
    };
  });
}
